import React, {memo, useCallback, useMemo} from "react";

import {
  List,
  ListItem,
  ListItemText,
  Toolbar,
  IconButton,
  Tooltip,
} from "@material-ui/core";
import { StatusBadge, Text, NavLink } from "../../components";
import { makeStyles } from "@material-ui/styles";
import { colors } from "../../theme/variables";
import _ from "lodash";
import { useWorkflowsByIds } from "../../data/workflow";
import { formatRelative } from "date-fns";
import RefreshIcon from "@material-ui/icons/Refresh";
import { getParentUrl } from "../../utils/helpers";
import { useEnv } from "../../plugins/env";
import { useQueryClient } from "react-query";


const useStyles = makeStyles({
  sidebar: {
    width: 360,
    minWidth: 360,
    border: "none",
    zIndex: 1,
    background: "#fff",
    display: "flex",
    flexDirection: "column",
    height: "100%",
  },
  toolbar: {
    backgroundColor: "#ffffff",
    borderBottom: "1px solid #e0e0e0",
    padding: "12px 16px",
    minHeight: "64px",
  },
  title: {
    fontWeight: 600,
    fontSize: "18px",
    color: "#1a1a1a",
    flex: 1,
  },
  list: {
    overflowY: "auto",
    flex: 1,
    padding: "8px",
  },
  listItem: {
    borderRadius: "6px",
    marginBottom: "4px",
    "&:hover": {
      backgroundColor: "#f5f5f5",
    },
  },
  workflowLink: {
    color: "#1976d2",
    textDecoration: "none",
    fontWeight: 500,
    fontSize: "14px",
    "&:hover": {
      textDecoration: "underline",
    },
  },
  secondaryInfo: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "12px",
    color: "#666666",
  },
  emptyState: {
    textAlign: "center",
    color: "#999999",
    fontSize: "14px",
    padding: "20px",
    fontStyle: "italic",
  },
});

 function ExecutionHistory({ run, setLoading, resetLoading }) {
  const classes = useStyles();
  const workflowRecords = useMemo(
    () => (run ? run.workflowRecords : []),
    [run]
  );
  const workflowIds = useMemo(
    () => workflowRecords.map((record) => `${record.workflowId}`),
    [workflowRecords]
  );
  const results =
    useWorkflowsByIds(workflowIds, {
      staleTime: 60000,
      // When switching between different Run History selections,
      // do not retain previous results so that we can show a loading state
      // until the new data (or error) arrives.
      keepPreviousData: false,
    }) || [];

  function useInvalidateWorkflows() {
    const { stack } = useEnv();
    const client = useQueryClient();

    return async function (workflowIds) {
      setLoading();
      await new Promise((resolve) => setTimeout(resolve, 2000)); // Ensure loader is shown for at least 2 seconds

      try {
        client.invalidateQueries({
          predicate: (query) =>
            query.queryKey[0] === stack &&
            query.queryKey[1] === "workflow" &&
            workflowIds.includes(query.queryKey[2]),
        });
      } finally {
        resetLoading();
      }
    };
  }

  const invalidateWorkflows = useInvalidateWorkflows();

  const handleRefresh = useCallback(() => {
    invalidateWorkflows(workflowIds);
  }, [invalidateWorkflows, workflowIds]);

  return (
    <div className={classes.sidebar}>
      <Toolbar className={classes.toolbar}>
        <Text level={0} className={classes.title}>
          Execution History
        </Text>
        <Tooltip title="Refresh">
          <IconButton onClick={handleRefresh} size="small">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Toolbar>
      <List className={classes.list}>
        {workflowIds.map((workflowId, idx) => {
          const result = results[idx];

          // Loading state
          if (!result || result.isLoading) {
            return (
              <ListItem key={workflowId} className={classes.listItem}>
                <ListItemText
                  primary={workflowId}
                  secondary={<div className={classes.secondaryInfo}>Loading…</div>}
                  secondaryTypographyProps={{ component: "div" }}
                />
              </ListItem>
            );
          }

          // Error state
          if (result.isError) {
            let message = "";
            try {
              const parsed = JSON.parse(result.error);
              message = parsed?.message || String(result.error);
            } catch (e) {
              message = String(result.error);
            }
            return (
              <ListItem key={workflowId} className={classes.listItem}>
                <ListItemText
                  primary={
                    <span className={classes.workflowLink}>{workflowId}</span>
                  }
                  secondary={
                    <div className={classes.secondaryInfo} style={{ color: colors.failure }}>
                      {message || "Failed to fetch workflow."}
                    </div>
                  }
                  secondaryTypographyProps={{ component: "div" }}
                />
              </ListItem>
            );
          }

          // Success state
          const workflow = result.data;
          return (
            <ListItem key={workflow.workflowId} className={classes.listItem}>
              <ListItemText
                primary={
                  <NavLink
                    absolutePath
                    path={`https://${getParentUrl()}/wolken/esd/w_flow?flowRedirectUrl=execution/${workflow.workflowId}`}
                    newTab
                    className={classes.workflowLink}
                  >
                    {workflow.workflowId}
                  </NavLink>
                }
                secondary={
                  <div className={classes.secondaryInfo}>
                    <StatusBadge status={workflow.status} size="small" />
                    <span>
                      {formatRelative(new Date(workflow.startTime), new Date())}
                    </span>
                  </div>
                }
                secondaryTypographyProps={{ component: "div" }}
              />
            </ListItem>
          );
        })}
        {_.isEmpty(workflowRecords) && (
          <div className={classes.emptyState}>No execution history.</div>
        )}
      </List>
    </div>
  );
}


export default memo(ExecutionHistory)
